import { useState, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  iconActive: string;
  end?: boolean;
  badge?: string;
}

const navItems: NavItem[] = [
  { to: '/inicio', label: 'Inicio', icon: 'ri-home-line', iconActive: 'ri-home-fill', end: true },
  { to: '/leyes', label: 'Leyes', icon: 'ri-book-2-line', iconActive: 'ri-book-2-fill' },
  { to: '/buscar', label: 'Buscar', icon: 'ri-search-line', iconActive: 'ri-search-fill' },
];

const navItems2: NavItem[] = [
  { to: '/feed', label: 'Noticias', icon: 'ri-file-list-3-line', iconActive: 'ri-file-list-3-fill' },
  { to: '/clasifica', label: 'Clasifica#', icon: 'ri-hashtag', iconActive: 'ri-hashtag', badge: 'TIGIE' },
];

const navItems3: NavItem[] = [
  { to: '/resaltados', label: 'Guardados', icon: 'ri-bookmark-line', iconActive: 'ri-bookmark-fill' },
  { to: '/perfil', label: 'Perfil', icon: 'ri-user-line', iconActive: 'ri-user-fill' },
];

function HamburgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <line x1="3" y1="5" x2="17" y2="5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="3" y1="15" x2="17" y2="15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function DrawerNavLink({ item, onClose }: { item: NavItem; onClose: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => `lex-drawer-link${isActive ? ' is-active' : ''}`}
      onClick={onClose}
      title={item.label}
    >
      {({ isActive }) => (
        <>
          <span className="lex-drawer-link-icon">
            <i className={isActive ? item.iconActive : item.icon} aria-hidden />
          </span>
          <span className="lex-drawer-link-label">
            {item.label}
            {item.badge && (
              <span style={{
                marginLeft: 8, fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                padding: '2px 6px', borderRadius: 4,
                background: 'var(--color-navy)', color: 'var(--color-bg)',
              }}>
                {item.badge}
              </span>
            )}
          </span>
        </>
      )}
    </NavLink>
  );
}

export function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarCollapsed((v) => !v), []);

  const handleLogout = () => {
    logout();
    closeDrawer();
    navigate('/login');
  };

  return (
    <div className="lex-app-shell">
      {/* Top header — mobile only (hidden on tablet via CSS) */}
      <header className="lex-top-header">
        <button
          type="button"
          className="lex-burger-btn"
          onClick={openDrawer}
          aria-label="Abrir menú"
        >
          <HamburgerIcon />
        </button>
        <span className="lex-top-header-logo">
          duma<span>s</span>
        </span>
      </header>

      {/* Drawer overlay — mobile only */}
      <div
        className={`lex-drawer-overlay${drawerOpen ? ' is-open' : ''}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Side drawer (mobile: slide-in) / Sidebar (tablet: always visible) */}
      <nav
        className={`lex-drawer${drawerOpen ? ' is-open' : ''}${sidebarCollapsed ? ' is-collapsed' : ''}`}
        role="navigation"
        aria-label="Menú principal"
      >
        {/* Brand — tablet only (hidden on mobile via CSS) */}
        <div className="lex-drawer-brand">
          <button
            type="button"
            className="lex-burger-btn lex-drawer-burger"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            <HamburgerIcon />
          </button>
          <span className="lex-top-header-logo lex-drawer-brand-logo">duma<span>s</span></span>
        </div>

        {/* User info */}
        <div className="lex-drawer-user">
          <p className="lex-drawer-user-name">{user?.name ?? 'Usuario'}</p>
          <p className="lex-drawer-user-email">{user?.email ?? ''}</p>
        </div>

        {/* Nav items */}
        <div className="lex-drawer-nav">
          {navItems.map((item) => (
            <DrawerNavLink key={item.to} item={item} onClose={closeDrawer} />
          ))}

          <div className="lex-drawer-sep" />

          {navItems2.map((item) => (
            <DrawerNavLink key={item.to} item={item} onClose={closeDrawer} />
          ))}

          <div className="lex-drawer-sep" />

          {navItems3.map((item) => (
            <DrawerNavLink key={item.to} item={item} onClose={closeDrawer} />
          ))}
        </div>

        {/* Logout */}
        <div className="lex-drawer-footer">
          <button
            type="button"
            className="lex-drawer-logout"
            onClick={handleLogout}
            title="Cerrar sesión"
          >
            <span className="lex-drawer-logout-icon">
              <i className="ri-logout-box-r-line" aria-hidden />
            </span>
            <span className="lex-drawer-link-label">Cerrar sesión</span>
          </button>
        </div>
      </nav>

      {/* Page content */}
      <div className="lex-page-wrap">
        <Outlet />
      </div>
    </div>
  );
}
